import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AxiosError, AxiosResponse } from "axios";
import { observer } from "mobx-react";

import { Profile } from "../../../stores/ProfileStore/Types";
import { CurrentUser } from "../../../stores/CurrentUserStore/Types";
import Settings from "./components/Settings";
import ProfileStore from "../../../stores/ProfileStore";
import SubscriptionInfo from "./components/SubscriptionInfo";
import EmailOptions from "./components/EmailOptions";
import Password from "./components/Password";
import DeleteAccount from "./components/DeleteAccount";
import { putRequest } from "../../../utils/httpRequest";
import { Errors, formErrors, notification } from "../../../utils/helpers";

export type ProfileSidebarView = "account" | "settings";

interface Props {
  currentUser: CurrentUser;
  initialView: ProfileSidebarView;
  isOpen: boolean;
  profilePicture: string;
  profileResource: Profile;
  profileStore: ProfileStore;
  getCurrentUser: () => void;
  handleShowLoggedInSideBar: () => void;
  handleShowProfileSidebar: (value: boolean) => void;
}

const settingsFromProfile = (profileResource: Profile) => ({
  communityAlertsEnabled: profileResource.community_alerts_enabled !== false,
  email: profileResource.email || "",
  firstName: profileResource.first_name || "",
  lastName: profileResource.last_name || "",
  locale: profileResource.locale || "en",
  selectedCountry: profileResource.country || "",
  weeklyLearningDigestEnabled:
    profileResource.weekly_learning_digest_enabled !== false,
  blogUpdatesEnabled: !!profileResource.blog_updates_enabled,
});

const ProfileSidebar: React.FC<Props> = ({
  currentUser,
  initialView,
  isOpen,
  profilePicture,
  profileResource,
  getCurrentUser,
  profileStore,
  handleShowLoggedInSideBar,
  handleShowProfileSidebar,
}) => {
  const [activeView, setActiveView] = useState<ProfileSidebarView>(initialView);
  const [settings, setSettings] = useState(() =>
    settingsFromProfile(profileResource),
  );
  const [emailEditing, setEmailEditing] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [userErrors, setUserErrors] = useState<any>({});
  const displayName = profileResource.name || currentUser.name;
  const xp = currentUser.kind === "current-user" ? currentUser.xp : 0;
  const initials = (() => {
    const parts = displayName?.trim().split(/\s+/).filter(Boolean) || [];

    if (parts.length > 1) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }

    const fallbackName = parts[0] || profileResource.first_name || "";
    return `${fallbackName[0] || ""}${
      fallbackName[fallbackName.length - 1] || ""
    }`.toUpperCase();
  })();

  useEffect(() => {
    if (isOpen) {
      setActiveView(initialView);
      setSettings(settingsFromProfile(profileResource));
      setEmailEditing(false);
      setShowPasswordModal(false);
      setShowDeleteModal(false);
      setUserErrors({});
    }
  }, [initialView, isOpen, profileResource.id]);

  const updateSetting = (key, value) => {
    setSettings((currentSettings) => ({
      ...currentSettings,
      [key]: value,
    }));
  };

  const handleFieldChange = (event, key) => {
    updateSetting(key, event.target.value);
  };

  const handleCountryChange = (_event, data) => {
    updateSetting("selectedCountry", data.value);
  };

  const handleLocaleChange = (_event, data) => {
    updateSetting("locale", data.value);
  };

  const toggleSetting = (key) => {
    setSettings((currentSettings) => ({
      ...currentSettings,
      [key]: !currentSettings[key],
    }));
  };

  // Cancel means "forget what I changed and let me out": the unsaved edits
  // are dropped and the sidebar closes. It used to reset the form and leave
  // the sidebar open, which looked like the button did nothing.
  const cancelSettings = () => {
    setSettings(settingsFromProfile(profileResource));
    setEmailEditing(false);
    setUserErrors({});
    handleShowProfileSidebar(false);
  };

  const saveSettings = () => {
    setSavingSettings(true);

    const data = {
      user: {
        first_name: settings.firstName,
        last_name: settings.lastName,
        email: settings.email,
        country: settings.selectedCountry,
        locale: settings.locale,
        name_required: true,
        weekly_learning_digest_enabled: settings.weeklyLearningDigestEnabled,
        blog_updates_enabled: settings.blogUpdatesEnabled,
        community_alerts_enabled: settings.communityAlertsEnabled,
      },
    };

    return putRequest(`/api/users/${profileResource.id}.json`, data)
      .then((response: AxiosResponse<Profile>) => {
        profileStore.setProfileResource(response.data);
        getCurrentUser();
        setSettings(settingsFromProfile(response.data));
        setEmailEditing(false);
        setUserErrors({});
        setSavingSettings(false);
        notification("Settings were successfully updated.", "notification");
      })
      .catch((error: AxiosError<{ errors: Errors }>) => {
        const errors = error?.response?.data?.errors
          ? formErrors(error.response.data.errors)
          : {};
        setUserErrors(errors);
        setSavingSettings(false);
      });
  };

  const showLoggedInSidebar = () => {
    handleShowProfileSidebar(false);
    handleShowLoggedInSideBar();
  };

  const subscriptionTitle = profileResource.subscription?.active
    ? profileResource.is_lifetime_subscription
      ? "Lifetime access"
      : "All Access Pass"
    : "No subscription";
  const subscriptionSummary = profileResource.subscription?.active
    ? profileResource.is_lifetime_subscription
      ? "Your lifetime pass is active."
      : "Your pass is active. Manage billing and card details from your account settings."
    : "You're on the free plan. Join the community, or get full access to every course.";
  const localeName =
    {
      en: "English",
      es: "Spanish",
      fr: "French",
      hi: "Hindi",
      ar: "Arabic",
      pt: "Portuguese",
      fi: "Finnish",
    }[profileResource.locale] || "English";

  return (
    <div
      className={`profile-home-sidebar-shell ${
        showPasswordModal || showDeleteModal ? "is-modal-open" : ""
      }`}
    >
      <div
        className={`profile-home-sidebar-backdrop ${isOpen ? "is-open" : ""}`}
        aria-hidden="true"
        onClick={() => handleShowProfileSidebar(false)}
      />
      <aside
        className={`profile-home-sidebar ${
          activeView === "settings" ? "show-settings" : ""
        } ${isOpen ? "is-open" : ""}`}
        id="profile_home_sidebar"
        role="dialog"
        aria-modal="true"
        aria-labelledby="profileHomeSidebarTitle"
        aria-hidden={!isOpen}
      >
        <div className="profile-home-sidebar__head">
          <button
            className="profile-home-sidebar__back"
            type="button"
            aria-label="Back to account"
            onClick={showLoggedInSidebar}
          >
            <svg aria-hidden="true">
              <use href="#icon-chevron-l" />
            </svg>
          </button>
          <h2 id="profileHomeSidebarTitle">
            {activeView === "settings" ? "Settings" : "Account"}
          </h2>
          <button
            className="profile-home-sidebar__close"
            type="button"
            aria-label="Close"
            onClick={() => handleShowProfileSidebar(false)}
          >
            <svg aria-hidden="true">
              <use href="#icon-close" />
            </svg>
          </button>
        </div>
        <div className="profile-home-sidebar__scroll">
          <div className="profile-home-sidebar__view profile-home-sidebar__view--account">
            <div className="profile-home-sidebar__profile">
              <div className="profile-home-sidebar__avatar">
                {profilePicture ? (
                  <img src={profilePicture} alt="" />
                ) : (
                  <span>{initials}</span>
                )}
              </div>
              <div className="profile-home-sidebar__identity">
                <strong>{displayName}</strong>
                <span>
                  {profileResource.rank_with_ordinal} in{" "}
                  {profileResource.country_name || "your leaderboard"} - {xp} XP
                </span>
              </div>
            </div>
            <nav
              className="profile-home-sidebar__links"
              aria-label="Account links"
            >
              <button type="button" onClick={() => setActiveView("settings")}>
                <svg aria-hidden="true">
                  <use href="#icon-cog" />
                </svg>
                Settings
              </button>
              <Link
                to="/achievements/badges"
                onClick={() => handleShowProfileSidebar(false)}
              >
                <svg aria-hidden="true">
                  <use href="#ln-badge" />
                </svg>
                Badges
              </Link>
              <a
                href="#certificates"
                onClick={() => handleShowProfileSidebar(false)}
              >
                <svg aria-hidden="true">
                  <use href="#ln-cert" />
                </svg>
                Certificates
              </a>
              <Link
                to="/courses"
                onClick={() => handleShowProfileSidebar(false)}
              >
                <svg aria-hidden="true">
                  <use href="#icon-book" />
                </svg>
                Browse courses
              </Link>
              <Link
                to="/referrals"
                onClick={() => handleShowProfileSidebar(false)}
              >
                <svg aria-hidden="true">
                  <use href="#icon-gift" />
                </svg>
                Refer a friend
              </Link>
            </nav>
            <a
              className="profile-home-sidebar__signout"
              rel="nofollow"
              data-method="delete"
              href="/session"
            >
              <svg aria-hidden="true">
                <use href="#icon-signout" />
              </svg>
              Sign out
            </a>
          </div>

          <div className="profile-home-sidebar__view profile-home-sidebar__view--settings">
            <section className="set-block">
              <div className="set-block-title">
                <svg aria-hidden="true">
                  <use href="#icon-person" />
                </svg>
                Profile
              </div>
              <Settings
                profileResource={profileResource}
                profileStore={profileStore}
                getCurrentUser={getCurrentUser}
                profilePicture={
                  currentUser.kind === "current-user"
                    ? currentUser.profile_picture
                    : ""
                }
                firstName={settings.firstName}
                lastName={settings.lastName}
                locale={settings.locale}
                selectedCountry={settings.selectedCountry}
                userErrors={userErrors}
                handleChange={handleFieldChange}
                handleCountryChange={handleCountryChange}
                handleLocaleChange={handleLocaleChange}
              />
            </section>

            <SubscriptionInfo
              profileResource={profileResource}
              profileStore={profileStore}
            />

            <section className="set-block">
              <div className="set-block-title">
                <svg aria-hidden="true">
                  <use href="#icon-receipt" />
                </svg>
                Receipts &amp; gifts
              </div>
              {profileResource.quaderno_billing_url && (
                <a
                  href={profileResource.quaderno_billing_url}
                  className="set-link"
                >
                  <svg aria-hidden="true">
                    <use href="#icon-receipt" />
                  </svg>
                  View receipts <span className="arr">→</span>
                </a>
              )}
              <Link to="/passes/year/gift" className="set-link">
                <svg aria-hidden="true">
                  <use href="#icon-gift" />
                </svg>
                Send a gift <span className="arr">→</span>
              </Link>
            </section>

            <EmailOptions
              weeklyLearningDigestEnabled={settings.weeklyLearningDigestEnabled}
              blogUpdatesEnabled={settings.blogUpdatesEnabled}
              communityAlertsEnabled={settings.communityAlertsEnabled}
              handleWeeklyLearningDigestToggle={() =>
                toggleSetting("weeklyLearningDigestEnabled")
              }
              handleBlogUpdatesToggle={() => toggleSetting("blogUpdatesEnabled")}
              handleCommunityAlertsToggle={() =>
                toggleSetting("communityAlertsEnabled")
              }
            />

            <section className="set-block">
              <div className="set-block-title">
                <svg aria-hidden="true">
                  <use href="#icon-lock" />
                </svg>
                Login &amp; security
              </div>
              <div className="set-field">
                <label htmlFor="profileSidebarEmail">Email</label>
                <div
                  className={`email-row ${emailEditing ? "is-editing" : ""}`}
                >
                  <input
                    className="set-input"
                    id="profileSidebarEmail"
                    type="email"
                    value={settings.email}
                    readOnly={!emailEditing}
                    onChange={(event) => handleFieldChange(event, "email")}
                  />
                  {!emailEditing && (
                    <button
                      type="button"
                      className="email-edit"
                      aria-label="Edit email"
                      onClick={() => setEmailEditing(true)}
                    >
                      <svg viewBox="0 0 16 16" aria-hidden="true">
                        <path
                          d="M11 2.5 L13.5 5 L5 13.5 L2.5 13.5 L2.5 11 Z"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  )}
                </div>
                {userErrors.email && (
                  <div className="set-error">{userErrors.email}</div>
                )}
              </div>
              <button
                type="button"
                className="set-link"
                onClick={() => setShowPasswordModal(true)}
              >
                <svg aria-hidden="true">
                  <use href="#icon-lock" />
                </svg>
                Change password <span className="arr">→</span>
              </button>
            </section>

            <section className="set-block set-block--last">
              <button
                className="set-danger"
                type="button"
                onClick={() => setShowDeleteModal(true)}
              >
                <svg aria-hidden="true">
                  <use href="#icon-danger" />
                </svg>
                Delete my account
              </button>
            </section>

            <div className="settings-actions">
              <button
                type="button"
                className="btn btn-ghost btn--sm"
                onClick={cancelSettings}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`btn btn-primary btn--sm ${
                  savingSettings ? "loading" : ""
                }`}
                disabled={savingSettings}
                onClick={saveSettings}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </aside>
      <Password
        userId={profileResource.id}
        showPasswordModal={showPasswordModal}
        handleShowPasswordModal={setShowPasswordModal}
      />
      <DeleteAccount
        profileResource={profileResource}
        showDeleteModal={showDeleteModal}
        handleShowDeleteModal={setShowDeleteModal}
      />
    </div>
  );
};

export default observer(ProfileSidebar);
